import { useData } from "../hooks/useData";
import { I } from "./Icons";
import { clsx, HeatMeter, StageBadge, Money, Avatar, fmtDate, dueText, dueOverdue } from "./Primitives";
import type { Partner, Principal, Deal } from "../types";

interface Props {
  id: string;
  go: (screen: string, id?: string) => void;
  openMinutes: (prefill?: { dealId?: string } | null) => void;
  partners: Record<string, Partner>;
}

export default function PrincipalStatus({ id, go, openMinutes, partners }: Props) {
  const { data } = useData<Principal & { deals: Deal[] }>(`/api/principals/${id}`, [id]);

  if (!data) return <div className="screen"><div style={{ color: "var(--muted)" }}>Loading...</div></div>;

  const deals = data.deals || [];
  const totalValue = deals.reduce((s, d) => s + (d.value_m || 0), 0);
  const activeDeals = deals.filter(d => d.stage !== "deployment");

  return (
    <div className="screen">
      <div className="crumbs" style={{ marginBottom: 16 }}>
        <a onClick={() => go("pipeline")}>Pipeline</a>
        <span className="dotsep">›</span>
        <a onClick={() => go("principals")}>Principals</a>
        <span className="dotsep">›</span>
        <span>{data.name}</span>
      </div>

      <div className="page-head">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8 }}>
            <div className="prin-logo" style={{ background: data.color, width: 48, height: 48, fontSize: 20 }}>
              {data.name.charAt(0)}
            </div>
            <div>
              <div className="page-title" style={{ marginBottom: 4 }}>{data.name}</div>
              <div style={{ fontSize: 13, color: "var(--muted)" }}>{data.base}</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ fontSize: 14, color: "var(--text)", lineHeight: 1.6, marginBottom: 20, maxWidth: 700 }}>
        {data.what}
      </div>

      <div className="prin-foot" style={{ marginBottom: 24, gap: 32 }}>
        <div className="prin-stat">
          <div className="ps-n mono">{deals.length}</div>
          <div className="ps-l">Active deals</div>
        </div>
        <div className="prin-stat">
          <div className="ps-n"><Money v={totalValue} /></div>
          <div className="ps-l">Total pipeline</div>
        </div>
        <div className="prin-stat">
          <div className="ps-n">{data.sector}</div>
          <div className="ps-l">Sector</div>
        </div>
      </div>

      <div className="section-label" style={{ marginBottom: 12 }}>Deals</div>
      <div className="card">
        <div className="pipe-table">
          <div className="pt-head">
            <span>Deal</span>
            <span>Operator</span>
            <span>Market</span>
            <span>Value</span>
            <span>Stage</span>
            <span>Heat</span>
            <span>Next</span>
          </div>
          {deals.map(d => (
            <div key={d.id} className="pt-row" onClick={() => go("project", d.id)}>
              <div className="pt-c-deal">
                <span className="ptr-name">{d.name}</span>
              </div>
              <div className="pt-c-rep">
                <span className="ptr-op">{d.operator_name || "—"}</span>
              </div>
              <div className="pt-c-mkt">
                {I.globe({ size: 13, w: 1.6 })}
                <span>{d.market}</span>
              </div>
              <div className="pt-c-val">
                {d.value_m ? <Money v={d.value_m} /> : "—"}
              </div>
              <div>
                <StageBadge stage={d.stage} />
              </div>
              <div>
                <HeatMeter level={d.heat} />
              </div>
              <div className={clsx("task-due", dueOverdue({ due: d.next_due }) && "overdue")}>
                {d.next_due ? dueText({ due: d.next_due, due_label: null }) : "—"}
              </div>
            </div>
          ))}
          {deals.length === 0 && (
            <div className="empty-mini">No deals yet — this principal is still warming up the bench.</div>
          )}
        </div>
      </div>
    </div>
  );
}
